import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import CollapseHeader from "../../../core/common/collapse-header";
import { toast } from "react-toastify";
import { apiService } from '../../../services/api';
import {
  ContaBancaria,
  CreateContaBancariaData,
  UpdateContaBancariaData,
  ContaBancariaFilters,
  AjustarSaldoContaData
} from '../../../types/financeiro';
import ScrollableTable from '../contracts/components/ScrollableTable';

const ContasBancarias = () => {
  // Estados principais
  const [contas, setContas] = useState<ContaBancaria[]>([]);
  const [filteredContas, setFilteredContas] = useState<ContaBancaria[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');

  // Estados para formulários
  const [contaSelecionada, setContaSelecionada] = useState<ContaBancaria | null>(null);
  const [formData, setFormData] = useState<CreateContaBancariaData>({
    nome: '',
    banco: '',
    agencia: '',
    conta: '',
    tipo_conta: 'corrente',
    principal: false,
    ativa: true,
    saldo_inicial: 0
  });
  const [updateFormData, setUpdateFormData] = useState<UpdateContaBancariaData>({});
  const [ajusteFormData, setAjusteFormData] = useState<AjustarSaldoContaData>({
    novo_saldo: 0,
    motivo: ''
  });

  // Estados para controle
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [ajusteId, setAjusteId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isAjustando, setIsAjustando] = useState<boolean>(false);

  // Estados para controle de erros nos modais
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [ajusteError, setAjusteError] = useState<string | null>(null);

  // Função para fechar modal programaticamente
  const closeModal = (modalId: string) => {
    const modalElement = document.getElementById(modalId);
    if (modalElement) {
      // Remove classes do Bootstrap
      modalElement.classList.remove('show');
      modalElement.style.display = 'none';
      modalElement.setAttribute('aria-hidden', 'true');
      modalElement.removeAttribute('aria-modal');
      modalElement.removeAttribute('role');

      // Remove backdrop
      const backdrop = document.querySelector('.modal-backdrop');
      if (backdrop) {
        backdrop.remove();
      }

      // Remove classe do body
      document.body.classList.remove('modal-open');
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('padding-right');
    }
  };

  // Função para formatar moeda
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Buscar contas bancárias
  const fetchContas = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getContasBancarias();

      if (response.success && response.data) {
        let contasArray: ContaBancaria[] = [];

        if (Array.isArray(response.data)) {
          contasArray = response.data as ContaBancaria[];
        } else {
          const responseData = response.data as any;
          contasArray = responseData.data || [];
        }

        const contasComKey = contasArray.map((conta: any) => ({
          ...conta,
          key: conta.id.toString(),
          saldo_atual: parseFloat(conta.saldo_atual) || 0
        }));

        setContas(contasComKey);
        setFilteredContas(contasComKey);
      } else {
        console.error('Resposta sem sucesso ou sem dados:', response);
        setError('Não foi possível obter os dados das contas bancárias');
      }
    } catch (err) {
      console.error('Erro ao buscar contas bancárias:', err);
      setError('Erro ao carregar dados das contas bancárias');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchContas();
  }, []);

  // Filtro de pesquisa com debounce
  useEffect(() => {
    setIsSearching(true);
    const debounceTimeout = setTimeout(() => {
      if (!searchText.trim()) {
        setFilteredContas(contas);
        setIsSearching(false);
        return;
      }

      const searchTerms = searchText.toLowerCase().trim().split(' ');

      const filtered = contas.filter(conta => {
        const searchableFields = [
          conta.nome,
          conta.banco,
          conta.agencia,
          conta.conta,
          conta.tipo_conta,
          conta.principal ? 'Principal' : 'Secundária',
          conta.ativa ? 'Ativo' : 'Inativo',
          formatCurrency(conta.saldo_atual)
        ].filter(Boolean).map(field => field?.toLowerCase() || '');

        return searchTerms.every(term =>
          searchableFields.some(field => field.includes(term))
        );
      });

      setFilteredContas(filtered);
      setIsSearching(false);
    }, 300);

    return () => clearTimeout(debounceTimeout);
  }, [searchText, contas]);

  // Função para limpar pesquisa
  const handleClearSearch = () => {
    setSearchText('');
  };

  // Função para editar conta
  const handleEditConta = async (id: number) => {
    try {
      setEditError(null);
      const conta = contas.find(c => c.id === id);
      if (conta) {
        setContaSelecionada(conta);
        setUpdateFormData({
          nome: conta.nome,
          banco: conta.banco,
          agencia: conta.agencia,
          conta: conta.conta,
          tipo_conta: conta.tipo_conta,
          principal: conta.principal,
          ativa: conta.ativa,
        });
      }
    } catch (err) {
      console.error('Erro ao preparar edição:', err);
      setEditError('Erro ao carregar dados da conta');
    }
  };

  // Função para submeter edição
  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contaSelecionada) return;

    try {
      setIsLoading(true);
      setEditError(null);

      const response = await apiService.updateContaBancaria(contaSelecionada.id, updateFormData);

      if (response.success) {
        const closeBtn = document.querySelector('#offcanvas_edit .btn-close') as HTMLElement;
        if (closeBtn) closeBtn.click();

        await fetchContas();
        toast.success('Conta bancária atualizada com sucesso!');
      } else {
        setEditError('Erro ao atualizar conta bancária: ' + response.message);
      }
    } catch (err: any) {
      console.error('Erro ao atualizar conta bancária:', err);
      setEditError('Erro ao atualizar conta bancária: ' + (err.message || 'Erro desconhecido'));
    } finally {
      setIsLoading(false);
    }
  };

  // Função para criar conta bancária
  const handleCreateConta = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsLoading(true);

      const response = await apiService.createContaBancaria(formData);

      if (response.success) {
        const closeBtn = document.querySelector('#offcanvas_add .btn-close') as HTMLElement;
        if (closeBtn) closeBtn.click();

        // Resetar formulário
        setFormData({
          nome: '',
          banco: '',
          agencia: '',
          conta: '',
          tipo_conta: 'corrente',
          principal: false,
          ativa: true,
          saldo_inicial: 0
        });

        await fetchContas();
        toast.success('Conta bancária criada com sucesso!');
      } else {
        setError('Erro ao criar conta bancária: ' + response.message);
      }
    } catch (err: any) {
      console.error('Erro ao criar conta bancária:', err);
      setError('Erro ao criar conta bancária: ' + (err.message || 'Erro desconhecido'));
    } finally {
      setIsLoading(false);
    }
  };

  // Função para ajustar saldo
  const handleAjustarSaldo = (id: number) => {
    const conta = contas.find(c => c.id === id);
    if (conta) {
      setAjusteId(id);
      setAjusteFormData({
        novo_saldo: conta.saldo_atual,
        motivo: ''
      });
      setAjusteError(null);
    }
  };

  // Confirmar ajuste de saldo
  const confirmAjustarSaldo = async () => {
    if (!ajusteId) return;

    try {
      setIsAjustando(true);
      setAjusteError(null);

      // 🔍 DEBUG: Log antes da chamada da API
      console.log('🔍 DEBUG confirmAjustarSaldo:', {
        ajusteId: ajusteId,
        ajusteFormData: ajusteFormData,
        urlQueSeraChamada: `/financeiro/contas_bancarias/ajustar_saldo.php/${ajusteId}`
      });

      const response = await apiService.ajustarSaldoConta(ajusteId, ajusteFormData);

      if (response.success) {
        closeModal('ajustar_saldo');
        await fetchContas();
        toast.success("Saldo ajustado com sucesso!");
        setAjusteId(null);
        setAjusteFormData({ novo_saldo: 0, motivo: '' });
      } else {
        setAjusteError('Erro ao ajustar saldo: ' + response.message);
      }
    } catch (err: any) {
      console.error('Erro ao ajustar saldo:', err);
      setAjusteError('Erro ao ajustar saldo: ' + (err.message || 'Erro desconhecido'));
    } finally {
      setIsAjustando(false);
    }
  };

  // Função para manipular mudanças no formulário
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'principal' ? (value === '1' ? true : false) : 
              name === 'ativa' ? (value === '1' ? true : false) : 
              name === 'saldo_inicial' ? parseFloat(value) || 0 : value
    });
  };

  // Função para manipular mudanças no formulário de edição
  const handleUpdateFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setUpdateFormData({
      ...updateFormData,
      [name]: name === 'principal' ? (value === '1' ? true : false) : 
              name === 'ativa' ? (value === '1' ? true : false) : value
    });
  };

  // Função para manipular mudanças no formulário de ajuste
  const handleAjusteFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setAjusteFormData({
      ...ajusteFormData,
      [name]: name === 'novo_saldo' ? parseFloat(value) || 0 : value
    });
  };

  // Definir as colunas da tabela
  const columns = [
    {
      title: "Nome",
      dataIndex: "nome",
      key: "nome",
      sorter: true,
      render: (text: string, record: ContaBancaria) => (
        <div className="d-flex flex-column">
          <span className="fw-semibold">{text}</span>
          <small className="text-muted">{record.banco}</small>
          {record.principal && (
            <span className="badge bg-primary badge-sm mt-1">Principal</span>
          )}
        </div>
      ),
    },
    {
      title: "Dados Bancários",
      dataIndex: "agencia",
      key: "dados_bancarios",
      render: (text: string, record: ContaBancaria) => (
        <div className="d-flex flex-column">
          <span>Ag: <strong>{record.agencia}</strong></span>
          <span>CC: <strong>{record.conta}</strong></span>
          <small className="text-muted text-capitalize">{record.tipo_conta}</small>
        </div>
      ),
    },
    {
      title: "Saldo Atual",
      dataIndex: "saldo_atual",
      key: "saldo_atual",
      sorter: true,
      render: (value: number) => (
        <div className="text-end">
          <div className={`fw-bold ${value >= 0 ? 'text-success' : 'text-danger'}`}>
            {formatCurrency(value)}
          </div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "ativo",
      key: "ativo",
      sorter: true,
      render: (ativo: number) => (
        <span className={`badge badge-pill ${ativo === 1 ? 'bg-danger' : 'bg-success'}`}>
          {ativo === 1 ? 'Inativo' : 'Ativo'}
        </span>
      ),
    },
    {
      title: "Ações",
      dataIndex: "acoes",
      key: "acoes",
      render: (text: string, record: ContaBancaria) => (
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-outline-primary"
            data-bs-toggle="offcanvas"
            data-bs-target="#offcanvas_edit"
            onClick={() => handleEditConta(record.id)}
          >
            <i className="ti ti-edit"></i>
          </button>
          <button
            className="btn btn-sm btn-outline-info"
            data-bs-toggle="modal"
            data-bs-target="#ajustar_saldo"
            onClick={() => handleAjustarSaldo(record.id)}
          >
            <i className="ti-wallet"></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-4">
                    <h4 className="page-title">
                      Contas Bancárias
                      <span className="count-title">{filteredContas.length} contas</span>
                    </h4>
                  </div>
                  <div className="col-8 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  {/* Search */}
                  <div className="row align-items-center">
                    <div className="col-sm-4">
                      <div className="search-input position-relative">
                        <div className="icon-form mb-3 mb-sm-0">
                          <span className="form-icon">
                            {isSearching ? (
                              <i className="spinner-border spinner-border-sm" role="status" />
                            ) : (
                              <i className="ti ti-search" />
                            )}
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Pesquisar Contas"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                          />
                          {searchText && (
                            <button
                              className="btn btn-sm position-absolute"
                              style={{ right: "10px", top: "50%", transform: "translateY(-50%)" }}
                              onClick={handleClearSearch}
                              type="button"
                            >
                              <i className="ti ti-x" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-8">
                      <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end">
                        <button
                          className="btn btn-primary"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_add"
                        >
                          <i className="ti ti-plus me-2"></i>
                          Nova Conta
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  {/* Tabela de Contas */}
                  {isLoading ? (
                    <div className="text-center p-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                      <p className="mt-2">Carregando contas bancárias...</p>
                    </div>
                  ) : error ? (
                    <div className="alert alert-danger" role="alert">
                      {error}
                    </div>
                  ) : !isSearching && searchText && filteredContas.length === 0 ? (
                    <div className="text-center py-5">
                      <div className="mb-3">
                        <i className="ti ti-search-off fs-3 text-muted"></i>
                      </div>
                      <h5 className="text-muted">Nenhuma conta encontrada</h5>
                      <p className="text-muted">Sua pesquisa por "{searchText}" não retornou resultados.</p>
                      <button
                        className="btn btn-outline-primary mt-2"
                        onClick={handleClearSearch}
                      >
                        <i className="ti ti-x me-1"></i>
                        Limpar pesquisa
                      </button>
                    </div>
                  ) : (
                    <ScrollableTable dataSource={filteredContas} columns={columns} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Adicionar Conta */}
      <div
        className="offcanvas offcanvas-end offcanvas-large"
        tabIndex={-1}
        id="offcanvas_add"
      >
        <div className="offcanvas-header border-bottom">
          <h4>Adicionar Nova Conta Bancária</h4>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          <form onSubmit={handleCreateConta}>
            <div className="row">
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="col-form-label">
                    Nome da Conta <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="nome"
                    value={formData.nome}
                    onChange={handleFormChange}
                    required
                    placeholder="Nome da conta bancária"
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="col-form-label">
                    Banco <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="banco"
                    value={formData.banco}
                    onChange={handleFormChange}
                    required
                    placeholder="Nome do banco"
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="col-form-label">
                    Tipo de Conta <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    name="tipo_conta"
                    value={formData.tipo_conta}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="corrente">Conta Corrente</option>
                    <option value="poupanca">Poupança</option>
                    <option value="investimento">Investimento</option>
                  </select>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="col-form-label">
                    Agência <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="agencia"
                    value={formData.agencia}
                    onChange={handleFormChange}
                    required
                    placeholder="Número da agência"
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="col-form-label">
                    Conta <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="conta"
                    value={formData.conta}
                    onChange={handleFormChange}
                    required
                    placeholder="Número da conta"
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="col-form-label">
                    Saldo Inicial
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    name="saldo_inicial"
                    value={formData.saldo_inicial}
                    onChange={handleFormChange}
                    step="0.01"
                    placeholder="0,00"
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="col-form-label">
                    Configurações
                  </label>
                  <div className="d-flex gap-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="principal"
                        checked={formData.principal === true}
                        onChange={(e) => setFormData({...formData, principal: e.target.checked})}
                      />
                      <label className="form-check-label">
                        Conta Principal
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="ativa"
                        checked={formData.ativa === true}
                        onChange={(e) => setFormData({...formData, ativa: e.target.checked})}
                      />
                      <label className="form-check-label">
                        Ativo
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center justify-content-end">
              <button
                type="button"
                data-bs-dismiss="offcanvas"
                className="btn btn-light me-2"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Criando...
                  </>
                ) : (
                  'Criar Conta'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal Editar Conta */}
      <div
        className="offcanvas offcanvas-end offcanvas-large"
        tabIndex={-1}
        id="offcanvas_edit"
      >
        <div className="offcanvas-header border-bottom">
          <h4>Editar Conta Bancária</h4>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          {isLoading ? (
            <div className="text-center p-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Carregando...</span>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmitEdit}>
              <div className="row">
                {editError && (
                  <div className="alert alert-danger mb-3" role="alert">
                    <i className="ti ti-alert-circle me-2"></i>
                    {editError}
                  </div>
                )}
                <div className="col-md-12">
                  <div className="mb-3">
                    <label className="col-form-label">
                      Nome da Conta <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="nome"
                      value={updateFormData.nome || ''}
                      onChange={handleUpdateFormChange}
                      required
                      placeholder="Nome da conta bancária"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="col-form-label">
                      Banco <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="banco"
                      value={updateFormData.banco || ''}
                      onChange={handleUpdateFormChange}
                      required
                      placeholder="Nome do banco"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="col-form-label">
                      Tipo de Conta <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-control"
                      name="tipo_conta"
                      value={updateFormData.tipo_conta || 'corrente'}
                      onChange={handleUpdateFormChange}
                      required
                    >
                      <option value="corrente">Conta Corrente</option>
                      <option value="poupanca">Poupança</option>
                      <option value="investimento">Investimento</option>
                    </select>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="col-form-label">
                      Agência <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="agencia"
                      value={updateFormData.agencia || ''}
                      onChange={handleUpdateFormChange}
                      required
                      placeholder="Número da agência"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="col-form-label">
                      Conta <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="conta"
                      value={updateFormData.conta || ''}
                      onChange={handleUpdateFormChange}
                      required
                      placeholder="Número da conta"
                    />
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="mb-3">
                    <label className="col-form-label">
                      Configurações
                    </label>
                    <div className="d-flex gap-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="principal"
                          checked={updateFormData.principal === true}
                          onChange={(e) => setUpdateFormData({...updateFormData, principal: e.target.checked})}
                        />
                        <label className="form-check-label">
                          Conta Principal
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="ativa"
                          checked={updateFormData.ativa === true}
                          onChange={(e) => setUpdateFormData({...updateFormData, ativa: e.target.checked})}
                        />
                        <label className="form-check-label">
                          Ativo
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center justify-content-end">
                <button
                  type="button"
                  data-bs-dismiss="offcanvas"
                  className="btn btn-light me-2"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Salvando...
                    </>
                  ) : (
                    'Salvar Alterações'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Modal Ajustar Saldo */}
      <div className="modal fade" id="ajustar_saldo" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Ajustar Saldo</h4>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={() => setAjusteError(null)}
              ></button>
            </div>
            <div className="modal-body">
              <div className="text-center mb-4">
                <div className="avatar avatar-xl bg-primary-light rounded-circle mb-3">
                  <i className="ti ti-wallet fs-36 text-primary" />
                </div>
                <h5 className="mb-2">Ajustar saldo da conta</h5>
                <p className="text-muted mb-0">
                  Digite o novo saldo e o motivo do ajuste.
                </p>
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Novo Saldo <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  value={ajusteFormData.novo_saldo}
                  onChange={(e) => setAjusteFormData({ ...ajusteFormData, novo_saldo: parseFloat(e.target.value) || 0 })}
                  step="0.01"
                  placeholder="0,00"
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Motivo do Ajuste <span className="text-danger">*</span>
                </label>
                <textarea
                  className="form-control"
                  value={ajusteFormData.motivo || ''}
                  onChange={(e) => setAjusteFormData({ ...ajusteFormData, motivo: e.target.value })}
                  rows={3}
                  placeholder="Descreva o motivo do ajuste de saldo..."
                  required
                />
              </div>

              {/* Exibir erro no modal se houver */}
              {ajusteError && (
                <div className="alert alert-danger" role="alert">
                  <i className="ti ti-alert-circle me-2"></i>
                  {ajusteError}
                </div>
              )}

              <div className="d-flex align-items-center justify-content-center mt-4">
                <Link
                  to="#"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  onClick={() => setAjusteError(null)}
                >
                  Cancelar
                </Link>
                <button
                  onClick={confirmAjustarSaldo}
                  className="btn btn-primary"
                  disabled={isAjustando || !ajusteFormData.motivo?.trim()}
                >
                  {isAjustando ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Ajustando...
                    </>
                  ) : (
                    'Confirmar Ajuste'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContasBancarias;